<template>
  <v-container>
    <v-row>
      <v-col cols="9">
        <v-skeleton-loader
          :loading="collectionPointLoading"
          :types="skeleton"
          type="article"
          class="mb-6"
          height="266"
        >
          <CollectionPointHeaderCard
            title="Žádost VN"
            detailType="requestVN"
            :data="collectionPoint"
          />
        </v-skeleton-loader>

        <v-skeleton-loader
          v-if="errorMessage"
          :loading="collectionPointLoading"
          :types="skeleton"
          type="article"
          class="mb-6"
          height="266"
        >
          <v-alert type="error">
            {{ errorMessage }}
          </v-alert>
        </v-skeleton-loader>

        <v-skeleton-loader
          v-else-if="!errorMessage"
          :loading="collectionPointLoading"
          :types="skeleton"
          type="article"
          class="mb-6"
          height="266"
        >
          <v-card class="elevation-12">
            <v-card-text class="px-9">
              <ValidationObserver
                ref="requestVNForm"
                v-slot="{ passes }"
              >
              <v-form rel="requestVNForm" @submit.prevent="passes(save)">
                <v-row>
                  <v-col>

                    <v-row class="request-value primary--text" no-gutters>
                      <v-col>
                        Specifikace technické změny
                      </v-col>
                    </v-row>

                    <v-row no-gutters>
                      <v-col>
                        <v-radio-group
                          v-model="requestVN.changeType"
                          dense
                          >
                          <v-radio
                            value="INCREASE_OF_RESERVED_POWER"
                            label="zvýšení rezervovaného příkonu">
                          </v-radio>
                          <v-radio
                            value="DECREASE_OF_RESERVED_POWER"
                            label="snížení rezervovaného příkonu">
                          </v-radio>
                        </v-radio-group>
                      </v-col>
                    </v-row>

                    <v-row class="request-value primary--text">
                      <v-col>
                        Rezervovaný příkon:
                      </v-col>
                    </v-row>

                    <v-row class="request-label" no-gutters>
                      <v-col cols="8">
                        Stávající hodnota: {{ collectionPoint.reservedPowerInput }} kW
                      </v-col>
                      <v-col cols="4">
                        Číslo TS: {{ collectionPoint.distributionSubstation }}
                      </v-col>
                    </v-row>

                    <v-row>
                      <v-col cols="7">
                        <VTextFieldWithValidation
                          v-model="requestVN.requestedReservedPower"
                          rules="required|numeric"
                          outlined
                          dense
                          label="Nově požadovaná hodnota"
                        />
                      </v-col>
                      <v-col class="py-5 request-label">
                        kW
                      </v-col>
                    </v-row>

                    <v-row>
                      <VDatePickerWithTextField
                        :disabled="datePickerLoading"
                        :loading="datePickerLoading"
                        :rules="{
                          'dateFormat': true,
                          'required': true,
                          'after': minForRequiredChangeDateDateFormatted,
                          'dateWorkDay': true,
                        }"
                        v-model="requestVN.requiredDateOfChange"
                        label="Požadovaný termín změny"
                        :min="minForRequiredChangeDateDateFormatted"
                        :allowedDates="allowedDates"
                        menuAttach=".detail-datepicker--capacity"
                        cols="7"
                        colClass="py-0 detail-datepicker detail-datepicker--capacity"
                      />
                    </v-row>

                    <v-row class="request-value primary--text mt-4" no-gutters>
                      <v-col>
                        Kontaktní informace žadatele
                      </v-col>
                    </v-row>
                    <v-row no-gutters>
                      <v-col>
                        <v-checkbox
                          @change="checkAddress()"
                          v-model="requestVN.addressIsTheSameAsInstallationAddress"
                        >
                        <template slot="label">
                          <span class="request-label primary--text">
                            adresa stejná s adresou odběrného místo
                          </span>
                        </template>
                        </v-checkbox>
                      </v-col>
                    </v-row>

                    <v-row>
                      <v-col cols="6" class="py-0">
                        <VTextFieldWithValidation
                          v-model="requestVN.contactDetails.name"
                          rules="required"
                          outlined
                          dense
                          label="Jméno"
                        />
                      </v-col>
                      <v-col class="py-0">
                        <VTextFieldWithValidation
                          v-model="requestVN.contactDetails.surname"
                          rules="required"
                          outlined
                          dense
                          label="Přijmení"
                        />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="6" class="py-0">
                        <VTextFieldWithValidation
                          v-model="requestVN.contactDetails.phone"
                          rules="required|phone"
                          outlined
                          dense
                          label="Telefon"
                          data-cy="phone"
                        />
                      </v-col>
                      <v-col class="py-0">
                        <VTextFieldWithValidation
                          v-model="requestVN.contactDetails.email"
                          rules="required|email"
                          outlined
                          dense
                          label="E-mail"
                        />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="6" class="py-0">
                        <VTextFieldWithValidation
                          v-model="requestVN.contactDetails.street"
                          rules="required"
                          outlined
                          dense
                          label="Ulice"
                          :disabled="requestVN.addressIsTheSameAsInstallationAddress"
                        />
                      </v-col>
                      <v-col class="py-0">
                        <v-row>
                          <v-col cols="6" class="py-0">
                            <VTextFieldWithValidation
                              v-model="requestVN.contactDetails.houseNumber"
                              :disabled="requestVN.addressIsTheSameAsInstallationAddress"
                              rules="required"
                              outlined
                              dense
                              label="Číslo popisné"
                            />
                          </v-col>
                          <v-col class="py-0">
                          <VTextFieldWithValidation
                              v-model="requestVN.contactDetails.postCode"
                              rules="required"
                              :disabled="requestVN.addressIsTheSameAsInstallationAddress"
                              outlined
                              dense
                              label="PSČ"
                            />
                          </v-col>
                        </v-row>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="6" class="py-0">
                        <VTextFieldWithValidation
                          v-model="requestVN.contactDetails.city"
                          outlined
                          rules="required"
                          dense
                          label="Město"
                          :disabled="requestVN.addressIsTheSameAsInstallationAddress"
                        />
                      </v-col>
                      <v-col cols="3" class="py-0">
                        <VTextFieldWithValidation
                          :disabled="requestVN.addressIsTheSameAsInstallationAddress"
                          v-model="requestVN.contactDetails.landRegistryNumber"
                          outlined
                          dense
                          label="Číslo orientační"
                        />
                      </v-col>
                    </v-row>

                    <v-row no-gutters>
                      <v-col class="request-value primary--text mb-3">
                        Zastoupení
                      </v-col>
                    </v-row>

                    <v-row>
                      <v-col cols="6" class="py-0">
                        <VTextFieldWithValidation
                          v-model="requestVN.contactDetails.substituteName"
                          rules="required"
                          outlined
                          dense
                          label="Jméno"
                        />
                      </v-col>
                      <v-col class="py-0">
                        <VTextFieldWithValidation
                          v-model="requestVN.contactDetails.substituteSurname"
                          rules="required"
                          outlined
                          dense
                          label="Přijmení"
                        />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col class="py-0">
                        <VTextFieldWithValidation
                          v-model="requestVN.contactDetails.substituteFunction"
                          rules="required"
                          outlined
                          dense
                          label="Funkce"
                        />
                      </v-col>
                      <v-col class="py-0">
                        <VFileInputWithValidation
                          v-model="contactSubstitutePowerOfAttorney"
                          :rules="{
                            required: false,
                            ext: ['pdf'],
                          }"
                          prepend-icon=""
                          append-icon="$file"
                          @click:append="
                            $event.target.offsetParent
                            .querySelector(`[class='v-file-input__text']`).click()
                          "
                          :clearable="false"
                          outlined
                          dense
                          label="Plná moc .PDF"
                          accept=".pdf"
                          @change="contactSubstitutePowerOfAttorneyUpload($event)"
                          data-cy="request-VN-file"
                        />
                      </v-col>
                    </v-row>

                    <v-row no-gutters>
                      <v-col class="request-value primary--text">
                        Požadavek na zvýšenou spolehlivost dodávky
                      </v-col>
                    </v-row>

                    <v-row no-gutters>
                      <v-col>
                        <v-radio-group row
                          v-model="requestVN.supplyReliability"
                          >
                          <v-radio
                            value="STANDARD"
                            label="Standardní">
                          </v-radio>
                          <v-radio
                            value="OVERSTANDARD"
                            label="Nadstandardní">
                          </v-radio>
                        </v-radio-group>
                      </v-col>
                    </v-row>

                    <v-row no-gutters>
                      <v-col class="request-value primary--text">
                        Kompenzace transformátoru
                      </v-col>
                    </v-row>

                    <v-row no-gutters>
                      <v-col>
                        <v-radio-group row
                          v-model="requestVN.powerFactor"
                          >
                          <v-radio
                            :value="true"
                            label="Ano">
                          </v-radio>
                          <v-radio
                            :value="false"
                            label="Ne">
                          </v-radio>
                        </v-radio-group>
                      </v-col>
                    </v-row>

                    <v-row no-gutters>
                      <v-col class="request-value primary--text">
                        Instalované transformátory
                      </v-col>
                    </v-row>

                    <v-row no-gutters>
                      <v-col>
                        <v-row no-gutters>
                          <v-col cols="3" align-self='center' class='request-label primary--text'>
                            Měrění na straně:
                          </v-col>
                          <v-col cols="9">
                            <v-radio-group row
                              v-model="requestVN.transformersDetails.measurement"
                            >
                              <v-radio
                                value="PRIMARY"
                                label="Primární">
                              </v-radio>
                              <v-radio
                                value="SECONDARY"
                                label="Sekundární">
                              </v-radio>
                            </v-radio-group>
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-col cols="2" class="py-0">
                            <VTextFieldWithValidation
                              v-model="requestVN.transformersDetails.quantity"
                              rules='numeric'
                              outlined
                              dense
                              label="Počet"
                            />
                          </v-col>
                          <v-col cols="1" class="request-label py-2 px-0">
                            ks
                          </v-col>
                          <v-col cols="8" class="py-0">
                            <VTextFieldWithValidation
                              v-model="requestVN.transformersDetails.installedPower"
                              rules='numeric'
                              outlined
                              dense
                              label="Celkový instalovaný výkon v transformaci"
                            />
                          </v-col>
                          <v-col cols="1" class="request-label py-2 px-0">
                            kVA
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-col cols="2" class="py-0">
                            <VTextFieldWithValidation
                              v-model="requestVN.transformersDetails.voltage"
                              rules='numeric'
                              outlined
                              dense
                              label="Napětí"
                            />
                          </v-col>
                          <v-col cols="1" class="request-label py-2 px-0">
                            kV
                          </v-col>
                          <v-col cols="8" class="py-0">
                            <VTextFieldWithValidation
                              v-model="requestVN.transformersDetails.reactiveLosses"
                              rules='numeric'
                              outlined
                              dense
                              label="Měsíční hodnota jalových ztrát v pásmu 1 hodiny"
                            />
                          </v-col>
                          <v-col cols="1" class="request-label py-2 px-0">
                            kVArh
                          </v-col>
                        </v-row>
                        <v-row no-gutters>
                          <v-col cols="5" align-self='center' class="request-label primary--text">
                            Sdílení výkonu transformátoru:
                          </v-col>
                          <v-col>
                            <v-radio-group row
                              v-model="requestVN.transformersDetails.sharing"
                            >
                              <v-radio
                                :value='true'
                                label="Ano">
                              </v-radio>
                              <v-radio
                                :value="false"
                                label="Ne">
                              </v-radio>
                            </v-radio-group>
                          </v-col>
                        </v-row>
                      </v-col>
                    </v-row>

                    <v-row no-gutters>
                      <v-col class="request-value primary--text" cols="4" align-self='center'>
                        Náhradní zdroj:
                      </v-col>
                      <v-col cols>
                        <v-radio-group row
                          v-model="requestVN.alternativeSource"
                        >
                          <v-radio
                            :value='true'
                            label="Ano">
                          </v-radio>
                          <v-radio
                            :value="false"
                            label="Ne">
                          </v-radio>
                        </v-radio-group>
                      </v-col>
                    </v-row>

                    <div v-show="requestVN.alternativeSource">
                      <v-row>
                        <v-col class="request-value primary--text">
                          Technické parametry
                        </v-col>
                      </v-row>

                      <v-row>
                        <v-col cols="6" class="py-0">
                          <VTextFieldWithValidation
                            v-model="requestVN.alternativeSourceDetails.kind"
                            :rules="{ required: requestVN.alternativeSource }"
                            outlined
                            dense
                            label="Druh náhradního zdroje"
                          />
                        </v-col>
                        <v-col class="py-0">
                          <VTextFieldWithValidation
                            v-model="requestVN.alternativeSourceDetails.type"
                            outlined
                            dense
                            label="Typ"
                          />
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="6" class="py-0">
                          <VTextFieldWithValidation
                            v-model="requestVN.alternativeSourceDetails.producer"
                            outlined
                            dense
                            label="Výrobce"
                          />
                        </v-col>
                        <v-col class="py-0">
                          <VTextFieldWithValidation
                            v-model="requestVN.alternativeSourceDetails.generatorType"
                            outlined
                            dense
                            label="Druh generátoru"
                          />
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="6" class="py-0">
                          <VTextFieldWithValidation
                            v-model="requestVN.alternativeSourceDetails.identicalDevices"
                            rules="numeric"
                            outlined
                            dense
                            label="Počet stejných zařízení"
                          />
                        </v-col>
                      </v-row>

                      <v-row no-gutters>
                        <v-col class="request-value primary--text">
                          Data jednoho zařízení
                        </v-col>
                      </v-row>

                      <v-row>
                        <v-col cols="5" class="pb-0">
                          <VTextFieldWithValidation
                            v-model="requestVN.alternativeSourceDetails.activePower"
                            :rules="{
                              numeric: true,
                              required: requestVN.alternativeSource,
                            }"
                            outlined
                            dense
                            label="Činný výkon"
                          />
                        </v-col>
                        <v-col cols="1" class="py-5 px-0 request-label">
                          kW
                        </v-col>
                        <v-col cols="5" class="pb-0">
                          <VTextFieldWithValidation
                            v-model="requestVN.alternativeSourceDetails.apparentPerformance"
                            rules="numeric"
                            outlined
                            dense
                            label="Zdánlivý výkon"
                          />
                        </v-col>
                        <v-col cols="1" class="py-5 px-0 request-label">
                          kVA
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="5" class="py-0">
                          <VTextFieldWithValidation
                            v-model="requestVN.alternativeSourceDetails.ratedVoltage"
                            rules="numeric"
                            outlined
                            dense
                            label="Jmenovité napětí"
                          />
                        </v-col>
                        <v-col cols="1" class="py-2 px-0 request-label">
                          V
                        </v-col>
                        <v-col cols="5" class="py-0">
                          <VTextFieldWithValidation
                            v-model="requestVN.alternativeSourceDetails.electricity"
                            rules="numeric"
                            outlined
                            dense
                            label="Proud"
                          />
                        </v-col>
                        <v-col cols="1" class="py-2 px-0 request-label">
                          A
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="5" class="py-0">
                          <VTextFieldWithValidation
                            v-model="requestVN.alternativeSourceDetails.transientGeneratorReactance"
                            rules="numeric"
                            outlined
                            dense
                            label="Přechodná reaktance generátoru"
                          />
                        </v-col>
                        <v-col cols="1" class="py-2 px-0 request-label">
                          Ω
                        </v-col>
                        <v-col cols="5" class="py-0">
                          <VTextFieldWithValidation
                            v-model="requestVN.alternativeSourceDetails.generatorSurgeReactance"
                            rules="numeric"
                            outlined
                            dense
                            label="Rázová reaktance generátoru"
                          />
                        </v-col>
                        <v-col cols="1" class="py-2 px-0 request-label">
                          Ω
                        </v-col>
                      </v-row>
                    </div>

                    <v-row no-gutters>
                      <v-col class="request-value primary--text">
                        Údaje o odběrném místě (nově požadované)
                      </v-col>
                    </v-row>

                    <v-row>
                      <v-col cols="5" class="pb-0">
                        <VTextFieldWithValidation
                          v-model="requestVN.supplyPointDetails.useOfSpace"
                          outlined
                          dense
                          label="Využití prostorů"
                        />
                      </v-col>
                      <v-col cols="1" class="py-2 px-0 request-label">
                      </v-col>
                      <v-col cols="5" class="pb-0">
                        <VTextFieldWithValidation
                          v-model="requestVN.supplyPointDetails.spaceSize"
                          rules="numeric"
                          outlined
                          dense
                          label="Velikost prostorů"
                        />
                      </v-col>
                      <v-col cols="1" class="py-5 px-0 request-label">
                        m2
                      </v-col>
                    </v-row>
                    <v-row class="py-0">
                      <v-col cols="5" class="py-0">
                        <VTextFieldWithValidation
                          v-model="requestVN.supplyPointDetails.electricHeating"
                          rules="numeric"
                          outlined
                          dense
                          label="el. top. PV AKU TČ"
                        />
                      </v-col>
                      <v-col cols="1" class="py-2 px-0 request-label">
                        kW
                      </v-col>
                      <v-col cols="5" class="py-0">
                        <VTextFieldWithValidation
                          v-model="requestVN.supplyPointDetails.airConditioning"
                          rules="numeric"
                          outlined
                          dense
                          label="klimatizace"
                        />
                      </v-col>
                      <v-col cols="1" class="py-2 px-0 request-label">
                        kW
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="5" class="py-0">
                        <VTextFieldWithValidation
                          v-model="requestVN.supplyPointDetails.engines"
                          rules="numeric"
                          outlined
                          dense
                          label="motory"
                        />
                      </v-col>
                      <v-col cols="1" class="py-2 px-0 request-label">
                        kW
                      </v-col>
                      <v-col cols="5" class="py-0">
                        <VTextFieldWithValidation
                          v-model="requestVN.supplyPointDetails.waterHeating"
                          rules="numeric"
                          outlined
                          dense
                          label="Ohřev vody AKU/PV"
                        />
                      </v-col>
                      <v-col cols="1" class="py-2 px-0 request-label">
                        kW
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="5" class="py-0">
                        <VTextFieldWithValidation
                          v-model="requestVN.supplyPointDetails.electromobility"
                          rules="numeric"
                          outlined
                          dense
                          label="elektromobilita"
                        />
                      </v-col>
                      <v-col cols="1" class="py-2 px-0 request-label">
                        kW
                      </v-col>
                      <v-col cols="5" class="py-0">
                        <VTextFieldWithValidation
                          v-model="requestVN.supplyPointDetails.other"
                          rules="numeric"
                          outlined
                          dense
                          label="ostatní"
                        />
                      </v-col>
                      <v-col cols="1" class="py-2 px-0 request-label">
                        kW
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="5" class="py-0">
                        <VTextFieldWithValidation
                          v-model="requestVN.supplyPointDetails.batteryStoragePerformance"
                          rules="numeric"
                          outlined
                          dense
                          label="bateriové úložiště - výkon"
                        />
                      </v-col>
                      <v-col cols="1" class="py-2 px-0 request-label">
                        kW
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="5" class="py-0">
                        <VTextFieldWithValidation
                          v-model="requestVN.supplyPointDetails.batteryStorageCapacity"
                          rules="numeric"
                          outlined
                          dense
                          label="bateriové úložiště - kapacita"
                        />
                      </v-col>
                      <v-col cols="1" class="py-2 px-0 request-label">
                        kWh
                      </v-col>
                    </v-row>

                    <v-row no-gutters>
                      <v-col class="request-label">
                        Celkem: {{ suplyPointSum }}kW
                      </v-col>
                    </v-row>

                    <v-row>
                      <v-col>
                        <VTextFieldWithValidation
                          v-model="requestVN.note"
                          counter="3000"
                          outlined
                          dense
                          label="Poznámka"
                        />
                      </v-col>
                    </v-row>

                    <v-row no-gutters>
                      <v-col cols="6">
                        <VFileInputWithValidation
                            v-model="attachment"
                            :rules="{
                              required: false,
                              ext: ['pdf'],
                              size: 10240,
                            }"
                            prepend-icon=""
                            append-icon="$file"
                            @click:append="
                              $event.target.offsetParent
                              .querySelector(`[class='v-file-input__text']`).click()
                            "
                            :clearable="false"
                            outlined
                            dense
                            label="Příloha .PDF"
                            accept=".pdf"
                            @change="attachmentUpload($event)"
                            data-cy="request-VN-file"
                          />
                      </v-col>
                    </v-row>

                    <v-row no-gutters>
                        <v-col>
                          <v-btn
                            depressed
                            type="submit"
                            height="40px"
                            min-width="125px"
                            color="primary"
                            :loading="loading"
                          >
                            Odeslat
                          </v-btn>
                        </v-col>
                      </v-row>

                  </v-col>
                </v-row>
              </v-form>
              </ValidationObserver>

            </v-card-text>
          </v-card>
        </v-skeleton-loader>
      </v-col>

      <CollectionPointRightSideCards
        :collectionPoint="collectionPoint"
        @searchInstallation="searchInstallation"
        @installationLoading="collectionPointLoading = $event"
        :eanErrorMessage="eanErrorMessage"
        :collectionPointLoading="collectionPointLoading"
      />
    </v-row>
  </v-container>
</template>

<script>
import CollectionPointHeaderCard from '@/components/CollectionPointHeaderCard.vue';
import { installationMixin } from '@/components/mixins/installationMixin';
import CollectionPointRightSideCards from '@/components/utils/CollectionPointRightSideCards.vue';
import { onInstallationSaveSuccess } from '@/components/mixins/saveSuccess';
import VDatePickerWithTextField from '@/components/VDatePickerWithTextField.vue';
import { mapActions } from 'vuex';

export default {
  name: 'RequestVNDetail',
  mixins: [installationMixin, onInstallationSaveSuccess],
  components: {
    CollectionPointHeaderCard,
    CollectionPointRightSideCards,
    VDatePickerWithTextField,
  },
  data() {
    return {
      loading: false,
      datePickerLoading: false,
      contactSubstitutePowerOfAttorney: null,
      requestVN: {
        changeType: 'INCREASE_OF_RESERVED_POWER',
        requestedReservedPower: 0,
        requiredDateOfChange: null,
        contactDetails: {
          addressIsTheSameAsInstallationAddress: false,
          name: null,
          surname: null,
          email: null,
          phone: '',
          street: null,
          city: null,
          houseNumber: null,
          landRegistryNumber: null,
          postCode: null,
          substituteName: null,
          substituteSurname: null,
          substituteFunction: null,
        },
        supplyReliability: 'STANDARD',
        powerFactor: true,
        transformersDetails: {
          measurement: 'SECONDARY',
          quantity: 0,
          installedPower: 0,
          voltage: 0,
          reactiveLosses: 0,
          sharing: false,
        },
        alternativeSource: false,
        alternativeSourceDetails: {
          kind: null,
          type: null,
          producer: null,
          generatorType: null,
          identicalDevices: 0,
          activePower: 0,
          apparentPerformance: 0,
          ratedVoltage: 0,
          electricity: 0,
          transientGeneratorReactance: 0,
          generatorSurgeReactance: 0,
        },
        supplyPointDetails: {
          useOfSpace: null,
          spaceSize: 0,
          electricHeating: 0,
          airConditioning: 0,
          engines: 0,
          batteryStoragePerformance: 0,
          batteryStorageCapacity: 0,
          waterHeating: 0,
          electromobility: 0,
          other: 0,
        },
        note: null,
      },
      attachment: null,
      requestedLengthsLoading: false,
      availableDates: [],
      errorMessage: null,
    };
  },
  computed: {
    suplyPointSum() {
      let sum = 0;
      Object.keys(this.requestVN.supplyPointDetails).forEach((key) => {
        if (!Number.isNaN(parseInt(this.requestVN.supplyPointDetails[key], 10)) && !['spaceSize', 'useOfSpace', 'batteryStorageCapacity'].includes(key)) {
          sum += parseInt(this.requestVN.supplyPointDetails[key], 10);
        }
      });
      return sum;
    },
    minForRequiredChangeDateDateFormatted() {
      return this.availableDates[1];
    },
  },
  watch: {
    collectionPointEan() {
      this.searchActiveRequest();
    },
  },
  methods: {
    ...mapActions({
      sendIndividualConnectionAgreementHv: 'requests/sendIndividualConnectionAgreementHv',
    }),
    formatDate(date) {
      if (!date) return null;

      const [year, month, day] = date.split('-');
      return `${day}.${month}.${year}`;
    },
    checkAddress() {
      this.requestVN.contactDetails.street = this.collectionPoint.street;
      this.requestVN.contactDetails.city = this.collectionPoint.city;
      this.requestVN.contactDetails.houseNumber = this.collectionPoint.houseNumber2;
      this.requestVN.contactDetails.landRegistryNumber = this.collectionPoint.houseNumber;
      this.requestVN.contactDetails.postCode = this.collectionPoint.postCode;
    },
    save() {
      this.loading = true;
      const requestData = {
        ean: this.collectionPoint.ean,
        individualConnectionAgreementHv: JSON.parse(JSON.stringify(this.requestVN)),
        contactSubstitutePowerOfAttorney: this.contactSubstitutePowerOfAttorney,
        attachment: this.attachment,
      };
      if (requestData.individualConnectionAgreementHv.alternativeSource === false) {
        delete requestData.individualConnectionAgreementHv.alternativeSourceDetails;
      }
      this.sendIndividualConnectionAgreementHv(requestData).then((response) => {
        this.onInstallationSaveSuccess(response);
      }).finally(() => {
        this.loading = false;
      });
    },
    getAllowedDates() {
      this.datePickerLoading = true;
      const actualDate = this.$dayjs(new Date()).format('YYYY-MM-DD');
      this.$http.get(`/requests/enumeration/calendar/working-days?dateSince=${actualDate}&months=6`)
        .then((response) => {
          this.availableDates = response.data;
        })
        .finally(() => {
          this.datePickerLoading = false;
        });
    },
    allowedDates(date) {
      return this.availableDates.includes(date);
    },
    attachmentUpload(event) {
      this.attachment = event;
    },
    contactSubstitutePowerOfAttorneyUpload(event) {
      this.contactSubstitutePowerOfAttorney = event;
    },
  },
  created() {
    this.datePickerLoading = true;
    this.getAllowedDates();
    const params = {
      checkAgreementExistence: true,
    };
    this.loadInstallation(params).then((response) => {
      if (response.data.activeAgreementRequest === true) {
        this.errorMessage = 'Pro zadaný EAN již evidujeme aktivní žádost.';
      }
      this.redirectByVoltageLevel(response.data, 'NN');
    }).finally(() => {
      this.datePickerLoading = false;
    });
  },
};
</script>
